import PageHeader from 'components/PageHeader.tsx';
import TabsGallery, { TabsGalleryRef, TabType } from 'components/TabsGallery.tsx';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout.tsx';
import { ADMIN_ITEMS } from 'pages/admin/adminItems';
import ComplianceBlockingRules from 'pages/configuration/ComplianceBlockingRules.tsx';
import ComplianceCheckExpiryConfig from 'pages/configuration/ComplianceCheckExpiryConfig.tsx';
import DataIntegrationsPage from 'pages/data-integration/DataIntegrationsPage.tsx';
import ProcessesPage from 'pages/processes/ProcessesPage.tsx';
import RelatedPartyRoleSettings from 'pages/configuration/RelatedPartyRoleSettings.tsx';
import RolesAndPermissionsPage from 'pages/roles-and-permissions/RolesAndPermissionsPage.tsx';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useIsSageHrEnabledQuery } from 'store/api/sage-hr-integration';
import { FeatureFlag } from 'types/features.ts';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import ClassifiersPage from '../classifiers/ClassifiersPage.tsx';
import ConfigurationPage from '../configuration/ConfigurationPage.tsx';
import CorporateRolePage from '../corporate-structure/CorporateRolePage.tsx';
import DocumentTemplatesTable from '../document-templates/DocumentTemplatesTable.tsx';
import FeatureFlagsTable from '../feature-flags/FeatureFlagsTable.tsx';

const DEFAULT_TAB = 'classifiers';

const Settings = () => {
    const tabsGalleryRef = React.createRef<TabsGalleryRef>();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = useIsNewDesign();

    // Redirect /settings to /settings/classifiers
    useEffect(() => {
        if (location.pathname === '/settings' || location.pathname === '/settings/') {
            navigate(`/settings/${DEFAULT_TAB}`, { replace: true });
        }
    }, [location, navigate]);

    const hasRolesAndPrivilegesPermission = useHasPermissions(['READ.ROLES_AND_PERMISSIONS']);
    const hasRunProcessesPermission = useHasPermissions(['RUN.PROCESSES']);
    const isIntegrationTiqEnabled = useIsFeatureEnabled(FeatureFlag.INTEGRATION_TIQ);
    const isClientIdentificationEnabled = useIsFeatureEnabled(FeatureFlag.CLIENT_IDENTIFICATION_AND_RISK_ASSESSMENT);
    const { data: isSageHrEnabled, isLoading: isSageHrLoading } = useIsSageHrEnabledQuery();

    const TABS = useMemo<TabType[]>(
        () => [
            {
                id: 'classifiers',
                label: <FormattedMessage id="menu.classifiers" />,
                component: <ClassifiersPage />,
            },
            {
                id: 'roles',
                label: <FormattedMessage id="menu.company-roles" />,
                component: <CorporateRolePage />,
            },
            {
                id: 'document-templates',
                label: <FormattedMessage id="menu.document-templates" />,
                component: <DocumentTemplatesTable />,
            },
            {
                id: 'feature-flags',
                label: <FormattedMessage id="menu.feature-flags" />,
                component: <FeatureFlagsTable />,
            },
            {
                id: 'configuration',
                label: <FormattedMessage id="menu.configuration" />,
                component: <ConfigurationPage />,
            },
            {
                id: 'roles-and-permissions',
                label: <FormattedMessage id="menu.roles-and-permissions" />,
                component: <RolesAndPermissionsPage />,
                hidden: !hasRolesAndPrivilegesPermission,
            },
            {
                id: 'data-integrations',
                label: <FormattedMessage id="menu.data-integrations" />,
                component: <DataIntegrationsPage />,
                hidden: !hasRolesAndPrivilegesPermission || (!isIntegrationTiqEnabled && !isSageHrEnabled),
            },
            {
                id: 'compliance',
                label: <FormattedMessage id="menu.compliance-blocking-rules" />,
                component: (
                    <div className="flex flex-col gap-8">
                        <ComplianceBlockingRules />
                        <ComplianceCheckExpiryConfig />
                        {isClientIdentificationEnabled && <RelatedPartyRoleSettings />}
                    </div>
                ),
            },
            {
                id: 'processes',
                label: <FormattedMessage id="menu.processes" />,
                component: <ProcessesPage />,
                hidden: !hasRunProcessesPermission,
            },
        ],
        [
            hasRolesAndPrivilegesPermission,
            hasRunProcessesPermission,
            isIntegrationTiqEnabled,
            isSageHrEnabled,
            isClientIdentificationEnabled,
        ]
    );

    // In the new design each tab is an Admin page of its own: the tab the path ends with, alone.
    if (isNew) {
        const tabId = location.pathname.split('/').filter(Boolean).at(-1);
        const tab = TABS.find((candidate) => candidate.id === tabId && !candidate.hidden);
        if (!tab) {
            // `/settings` itself waits for the redirect above, and Data integration for Sage HR's answer; a
            // tab the user cannot see sends them home.
            return tabId === 'settings' || isSageHrLoading ? null : <Navigate to="/" replace />;
        }
        return (
            <PaddedLayout>
                {/* One column, as on a list page: its gap sets the title apart from the tab, and a tab that is a
                    bare list hands over its toolbar and its table as two siblings, which it spaces too. */}
                <div className="flex flex-col gap-4">
                    <PageHeader
                        titleId={ADMIN_ITEMS.find((item) => item.id === tab.id)?.labelId}
                        showBreadcrumb
                        hostsList
                    />
                    {tab.component}
                </div>
            </PaddedLayout>
        );
    }

    return (
        <PaddedLayout>
            <PageHeader titleId={'menu.settings'} showBreadcrumb />
            <>
                <TabsGallery ref={tabsGalleryRef} tabs={TABS} tabGroupId="settings-page" usePathBasedRouting />
            </>
        </PaddedLayout>
    );
};

export default Settings;
